// Reconstruct View - 3D Fragment Assembly Workspace
import { getAssets, createAsset } from '../firebase/db.js'; // Import createAsset
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Import storage functions
import router from '../router.js';
import { toast } from '../components/Toast.js';
import { Viewer3D } from '../components/Viewer3D.js';
import { generateAssemblyPDF, createSimpleConnections } from '../utils/assemblyDiagram.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { t } from '../i18n.js';
import { getUserCredits, deductCredits, ASSEMBLY_CREDIT_COST } from '../firebase/credits.js';
import { showCreditsModal, updateNavCreditBadge } from '../components/CreditsModal.js';

let viewer3d = null;

/**
 * Create reconstruct view
 * @param {Object} site 
 * @param {Array} assets 
 * @returns {string} HTML string
 */
export function createReconstructView(site, assets = []) {
  if (!site) {
    return `
      <div class="page">
        <div class="container">
          <div class="empty-state">
            <div class="empty-state-icon">❌</div>
            <h2 class="empty-state-title">${t('app.error')}</h2>
            <a href="#/dashboard" class="btn btn-primary">${t('nav.dashboard')}</a>
          </div>
        </div>
      </div>
    `;
  }

  return `
    <div class="page">
      <div class="container">
        <!-- Breadcrumb -->
        <div class="breadcrumb">
          <a href="#/dashboard">${t('nav.dashboard')}</a>
          <span class="breadcrumb-sep">›</span>
          <a href="#/site/${site.id}">${escapeHtml(site.name)}</a>
          <span class="breadcrumb-sep">›</span>
          <span>${t('recon.title')}</span>
        </div>
        
        <div class="page-header">
          <h1 class="page-title">🧩 ${t('recon.title')}</h1>
          <p class="page-subtitle">${t('recon.subtitle')}</p>
        </div>

        <!-- Mobile Warning Overlay -->
        <div id="mobile-assembly-warning" class="mobile-warning-overlay hidden">
          <div class="mobile-warning-content glass-effect">
            <span class="warning-icon">⚠️</span>
            <h3>${t('recon.title')}</h3>
            <p>${t('recon.mobileWarning')}</p>
            <div class="warning-actions">
              <button class="btn btn-primary" id="try-anyway-btn">${t('recon.tryAnyway')}</button>
              <a href="#/site/${site.id}" class="btn btn-ghost">${t('nav.dashboard')}</a>
            </div>
          </div>
        </div>
        
        <div class="reconstruct-layout">
          <!-- Fragment Selection Panel -->
          <div class="fragment-panel">
            <h3 class="mb-4">${t('recon.selectParts')}</h3>
            
            ${assets.length > 0 ? `
              <div class="fragment-list" id="fragment-list">
                ${assets.map(asset => createFragmentItem(asset)).join('')}
              </div>
              
              <div class="fragment-panel-footer">
                <div class="selected-count">
                  <span id="selected-count">0</span>
                </div>
                <button class="btn btn-secondary btn-sm" id="clear-selection">${t('recon.clearDesk')}</button>
              </div>
            ` : `
              <div class="empty-state" style="padding: var(--space-6);">
                <p class="text-muted text-sm">${t('recon.emptyLib')}</p>
                <a href="#/site/${site.id}/assets" class="btn btn-secondary btn-sm mt-4">${t('site.tabAssets')}</a>
              </div>
            `}
          </div>
          
          <!-- Main Viewer Area -->
          <div class="reconstruct-main">
            <div class="viewer-3d" id="reconstruct-viewer"></div>
            
            <div class="reconstruct-actions flex-mobile-column">
              <button class="btn btn-secondary flex-1" id="preview-btn" ${assets.length === 0 ? 'disabled' : ''}>
                👁️ ${t('recon.preview')}
              </button>
              <button class="btn btn-primary btn-lg flex-1" id="reassemble-btn" ${assets.length === 0 ? 'disabled' : ''}>
                🧩 ${t('recon.title')}
              </button>
              <button class="btn btn-secondary flex-1" id="reset-view-btn">
                ↺ ${t('assets.rotation')}
              </button>
            </div>
            
            <!-- Reconstruction Status -->
            <div class="reconstruct-status hidden" id="reconstruct-status">
              <div class="status-animation">
                <div class="status-spinner"></div>
              </div>
              <div class="status-text">
                <h3>${t('recon.processingTitle')}</h3>
                <p id="status-message">${t('recon.processingDesc')}</p>
              </div>
              <div class="status-progress">
                <div class="progress">
                  <div class="progress-bar" id="status-progress-bar"></div>
                </div>
              </div>
            </div>
            
            <!-- Result -->
            <div class="reconstruct-result hidden" id="reconstruct-result">
              <div class="result-success">
                <span class="result-icon">✅</span>
                <h3>${t('recon.done')}</h3>
              </div>
              <div class="result-actions flex justify-center gap-4 mt-6 flex-mobile-column">
                <button class="btn btn-primary flex-1" id="save-result-btn">
                    <span>⬇️</span>
                    <span>${t('recon.download')}</span>
                </button>
                <button class="btn btn-secondary flex-1" id="add-to-library-btn">
                    <span>📚</span>
                    <span>${t('recon.saveToLib')}</span>
                </button>
                <button class="btn btn-secondary flex-1" id="download-pdf-btn" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                    <span>📄</span>
                    <span>${t('recon.downloadPDF')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <style>
      .breadcrumb {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-6);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }
      
      .breadcrumb a {
        color: var(--color-text-secondary);
      }
      
      .reconstruct-layout {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: var(--space-6);
        min-height: 600px;
      }
      
      .fragment-panel {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-xl);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
      }
      
      .fragment-list {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .fragment-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--color-bg-tertiary);
        border: 2px solid transparent;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .fragment-item:hover {
        background: var(--color-bg-elevated);
      }
      
      .fragment-item.selected {
        border-color: var(--color-accent-primary);
        background: rgba(212, 168, 83, 0.1);
      }
      
      .fragment-item.selected::before {
        content: '✓';
        position: absolute;
        right: var(--space-2);
        color: var(--color-accent-primary);
        font-weight: bold;
      }
      
      .fragment-item {
        position: relative;
      }
      
      .fragment-thumb {
        width: 48px;
        height: 48px;
        background: var(--color-bg-primary);
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
      }
      
      .fragment-info {
        flex: 1;
        overflow: hidden;
      }
      
      .fragment-name {
        font-size: var(--text-sm);
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .fragment-format {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
      }
      
      .fragment-panel-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: var(--space-4);
        margin-top: var(--space-4);
        border-top: 1px solid var(--color-border);
      }
      
      .selected-count {
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }
      
      .selected-count span {
        color: var(--color-accent-primary);
        font-weight: 600;
      }
      
      .reconstruct-main {
        display: flex;
        flex-direction: column;
        gap: var(--space-5);
      }
      
      .reconstruct-actions {
        display: flex;
        justify-content: center;
        gap: var(--space-4);
      }
      
      .reconstruct-status {
        padding: var(--space-6);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-xl);
        text-align: center;
      }
      
      .reconstruct-status.hidden,
      .reconstruct-result.hidden {
        display: none;
      }
      
      .status-animation {
        margin-bottom: var(--space-4);
      }
      
      .status-spinner {
        width: 60px;
        height: 60px;
        margin: 0 auto;
        border: 4px solid var(--color-border);
        border-top-color: var(--color-accent-primary);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      .status-text h3 {
        margin-bottom: var(--space-2);
      }
      
      .status-text p {
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
      }
      
      .status-progress {
        margin-top: var(--space-4);
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }
      
      .reconstruct-result {
        padding: var(--space-6);
        background: linear-gradient(135deg, rgba(74, 222, 128, 0.1), rgba(74, 222, 128, 0.05));
        border: 1px solid rgba(74, 222, 128, 0.3);
        border-radius: var(--radius-xl);
        text-align: center;
      }
      
      .result-success {
        margin-bottom: var(--space-6);
      }
      
      .result-icon {
        font-size: 3rem;
        display: block;
        margin-bottom: var(--space-3);
      }
      
      .result-actions {
        display: flex;
        justify-content: center;
        gap: var(--space-4);
      }
      
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @media (max-width: 900px) {
        .reconstruct-layout {
          grid-template-columns: 1fr;
          min-height: auto;
        }
        
        .fragment-panel {
          max-height: 300px;
          order: 2; /* Put selection below viewer on mobile for better context */
        }

        .reconstruct-main {
          order: 1;
        }

        #reconstruct-viewer {
          height: 50vh;
        }

        .reconstruct-actions {
          gap: var(--space-2);
        }
      }

      /* Mobile Warning Styles */
      .mobile-warning-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(10px);
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-6);
      }

      .mobile-warning-overlay.hidden {
        display: none;
      }

      .mobile-warning-content {
        max-width: 400px;
        text-align: center;
        padding: var(--space-8);
        border-radius: var(--radius-2xl);
        border: 1px solid rgba(255, 255, 255, 0.1);
        animation: fadeInScale 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      }

      .warning-icon {
        font-size: 4rem;
        display: block;
        margin-bottom: var(--space-4);
      }

      .warning-actions {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        margin-top: var(--space-6);
      }

      @keyframes fadeInScale {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }
    </style>
  `;
}

/**
 * Create fragment item HTML
 * @param {Object} asset 
 * @returns {string}
 */
function createFragmentItem(asset) {
  const formatIcons = {
    obj: '📦',
    stl: '🔷',
    ply: '🌐',
    gltf: '🎨',
    glb: '🎨'
  };

  const artifactId = asset.artifactId !== undefined ? `#${asset.artifactId}` : '';

  return `
    <div class="fragment-item" data-asset-id="${asset.id}" data-url="${asset.url}" data-format="${asset.format}" data-artifact-id="${asset.artifactId || ''}">
      <div class="fragment-thumb">${formatIcons[asset.format] || '🗿'}</div>
      <div class="fragment-info">
        <div class="fragment-name">${escapeHtml(asset.name)}</div>
        <div class="fragment-format">${asset.format} ${artifactId}</div>
      </div>
    </div>
  `;
}

/**
 * Initialize reconstruct view
 * @param {Object} site 
 * @param {Array} assets 
 */
export function initReconstructView(site, assets = []) {
  if (!site) return;

  // Mobile Detection & Warning
  const isMobile = window.innerWidth <= 768;
  const warningOverlay = document.getElementById('mobile-assembly-warning');
  const hasDismissedWarning = sessionStorage.getItem('dismissed_mobile_warning');

  if (isMobile && warningOverlay && !hasDismissedWarning) {
    warningOverlay.classList.remove('hidden');
  }

  document.getElementById('try-anyway-btn')?.addEventListener('click', () => {
    warningOverlay.classList.add('hidden');
    sessionStorage.setItem('dismissed_mobile_warning', 'true');
    toast.info('Mobile mode activated');
  });

  const selectedAssets = new Set();
  const viewerContainer = document.getElementById('reconstruct-viewer');

  // Initialize 3D viewer
  if (viewerContainer) {
    viewer3d = new Viewer3D(viewerContainer);
  }

  // Fragment selection
  document.querySelectorAll('.fragment-item').forEach(item => {
    item.addEventListener('click', () => {
      const assetId = item.dataset.assetId;

      if (selectedAssets.has(assetId)) {
        selectedAssets.delete(assetId);
        item.classList.remove('selected');
      } else {
        selectedAssets.add(assetId);
        item.classList.add('selected');
      }

      updateSelectedCount();
    });
  });

  // Clear selection
  document.getElementById('clear-selection')?.addEventListener('click', () => {
    selectedAssets.clear();
    document.querySelectorAll('.fragment-item').forEach(item => {
      item.classList.remove('selected');
    });
    updateSelectedCount();
  });

  // Preview selected
  document.getElementById('preview-btn')?.addEventListener('click', async () => {
    if (selectedAssets.size === 0) {
      toast.warning('Please select at least one fragment');
      return;
    }

    // Load first selected asset into viewer
    const firstId = Array.from(selectedAssets)[0];
    const item = document.querySelector(`[data-asset-id="${firstId}"]`);
    if (item && viewer3d) {
      try {
        await viewer3d.loadModel(item.dataset.url, item.dataset.format);
        toast.info('Previewing selected fragment');
      } catch (error) {
        console.error('Preview error:', error);
        toast.error('Failed to load preview');
      }
    }
  });

  // Reassemble button — check credits before running
  document.getElementById('reassemble-btn')?.addEventListener('click', async () => {
    if (selectedAssets.size < 2) {
      toast.warning(t('recon.preview') + ' — ' + 'Select at least 2 fragments');
      return;
    }

    // Credit gate
    const credits = await getUserCredits(site.userId ?? '');
    if (credits < ASSEMBLY_CREDIT_COST) {
      toast.error(t('credits.insufficient'));
      showCreditsModal();
      return;
    }

    // Deduct first, then assemble
    const result = await deductCredits(site.userId ?? '', ASSEMBLY_CREDIT_COST, 'Assembly operation');
    if (!result.success) {
      toast.error(t('credits.insufficient'));
      showCreditsModal();
      return;
    }

    // Update nav badge immediately
    updateNavCreditBadge(result.balance);

    // Call REAL reconstruction API
    await performReconstruction(site, assets.filter(a => selectedAssets.has(a.id)));
  });

  // Reset view
  document.getElementById('reset-view-btn')?.addEventListener('click', () => {
    if (viewer3d) {
      viewer3d.resetCamera();
    }
  });

  // Save result
  document.getElementById('save-result-btn')?.addEventListener('click', () => {
    toast.success('Reconstructed model saved! (Demo)');
  });

  // Attach to log
  document.getElementById('attach-to-log-btn')?.addEventListener('click', () => {
    router.navigate(`/site/${site.id}/logbook`);
    toast.info('Navigate to logbook to create an entry with this reconstruction');
  });

  function updateSelectedCount() {
    const countEl = document.getElementById('selected-count');
    if (countEl) countEl.textContent = selectedAssets.size;
  }
}

/**
 * Simulate reconstruction process
 * @param {Array} selectedFragments 
 */
/**
 * Perform REAL reconstruction via Python API
 * @param {Array} selectedFragments 
 */
// Color Palette for Assembly
const FRAGMENT_COLORS = [
  0xFF6B6B, // Red
  0x4ECDC4, // Teal
  0x45B7D1, // Cyan
  0xFFA07A, // Light Salmon
  0x98D8C8, // Mint
  0xF7DC6F, // Yellow
  0xBB8FCE, // Purple
  0xF1948A, // Pink
  0x85C1E9, // Light Blue
  0x82E0AA, // Light Green
  0xD98880, // Brick Red
  0x7FB3D5, // Steel Blue
  0xC39BD3, // Violet
  0x7DCEA0, // Medium Green
  0xF8C471, // Orange
  0xE59866, // Burnt Orange
  0x1ABC9C, // Strong Teal
  0x2ECC71, // Strong Green
  0x3498DB, // Strong Blue
  0x9B59B6  // Strong Purple
];

async function performReconstruction(site, selectedFragments) {
  const statusEl = document.getElementById('reconstruct-status');
  const resultEl = document.getElementById('reconstruct-result');
  const progressBar = document.getElementById('status-progress-bar');
  const statusMessage = document.getElementById('status-message');

  // Show status
  statusEl.classList.remove('hidden');
  resultEl.classList.add('hidden');
  statusMessage.textContent = t('recon.loading1');
  progressBar.style.width = '10%';

  try {
    // Prepare payload
    const payload = {
      assets: selectedFragments.map(f => ({
        url: f.url,
        format: f.format || 'obj'
      }))
    };

    statusMessage.textContent = t('recon.loading2');
    progressBar.style.width = '30%';

    // Call Python Server (Render Production)
    const response = await fetch('https://tot-archaeohub-demo.onrender.com/reconstruct', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Server Error: ${errText}`);
    }

    statusMessage.textContent = t('recon.loading3');
    progressBar.style.width = '70%';

    // Get the result blob (the new 3D model)
    const blob = await response.blob();
    statusMessage.textContent = t('recon.loading4');
    progressBar.style.width = '90%';

    // Create object URL for the new model
    const objectUrl = URL.createObjectURL(blob);

    // Load it
    if (viewer3d) {
      viewer3d.clearModels(); // Clear fragments
      try {
        // Determine format from content-type or default to obj
        const loadedModel = await viewer3d.loadModel(objectUrl, 'obj');

        // ==== ASSIGN IDs TO PARTS ====
        // The server returns a Scene with named parts: "part_0", "part_1", etc.
        // We know selectedFragments holds the original data in the same order.
        if (loadedModel) {
          loadedModel.traverse((child) => {
            if (child.isMesh && child.name.startsWith('part_')) {
              const indexStr = child.name.split('_')[1];
              const index = parseInt(indexStr, 10);

              if (!isNaN(index) && index >= 0 && index < selectedFragments.length) {
                const originalFragment = selectedFragments[index];
                child.userData.artifactId = originalFragment.artifactId;
                child.userData.name = originalFragment.name;

                // Ensure material allows raycasting/picking
                // Sometimes materials from OBJLoader are not ideal for interaction
                child.material.side = 2; // DoubleSide

                // APPLY COLOR VISUALLY
                // Use the same palette as the PDF so it matches
                const color = FRAGMENT_COLORS[index % FRAGMENT_COLORS.length];
                viewer3d.setModelColor(child, color);
              }
            }
          });
        }

      } catch (error) {
        console.error('Viewer Load error:', error);
        toast.error('Sonuç görüntülenemedi');
      }
    }

    // Show result UI

    statusEl.classList.add('hidden');
    resultEl.classList.remove('hidden');
    progressBar.style.width = '100%';
    toast.success('Birleştirme Tamamlandı!');



    // Update "Save" button to download the blob
    const saveBtn = document.getElementById('save-result-btn');
    if (saveBtn) {
      // Remove old listeners
      const newBtn = saveBtn.cloneNode(true);
      saveBtn.parentNode.replaceChild(newBtn, saveBtn);

      newBtn.addEventListener('click', () => {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = 'reconstructed_model.obj';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    }

    // Update "Add to Library" button
    const addToLibBtn = document.getElementById('add-to-library-btn');
    if (addToLibBtn) {
      const newBtn = addToLibBtn.cloneNode(true);
      addToLibBtn.parentNode.replaceChild(newBtn, addToLibBtn);

      newBtn.addEventListener('click', async () => {
        const siteIdRegex = /site\/([a-zA-Z0-9]+)/;
        const match = window.location.hash.match(siteIdRegex);
        if (!match) {
          toast.error('Could not determine site ID');
          return;
        }
        const siteId = match[1];

        try {
          newBtn.disabled = true;
          newBtn.textContent = t('recon.packagingLib');

          const storage = getStorage();
          const timestamp = Date.now();

          // 1. Export Scene to GLB (Colors + Geometry)
          // We assume viewer3d currently holds the colored assembly
          const exporter = new GLTFExporter();

          // Promise wrapper for export
          const glbBlob = await new Promise((resolve, reject) => {
            exporter.parse(
              viewer3d.scene,
              (result) => {
                const output = result instanceof ArrayBuffer ? result : JSON.stringify(result, null, 2);
                const blob = new Blob([output], { type: 'model/gltf-binary' });
                resolve(blob);
              },
              (error) => reject(error),
              { binary: true }
            );
          });

          // 2. Generate PDF Blob
          // We need connection data and images (which we have in memory from the generation step?)
          // actually, we might not have them if user didn't click "Preview" first.
          // Fallback: If no PDF data generated yet, we skip PDF upload or do a quick text-only one.
          // IDEALLY: We stored 'reconstructionData' in a closure or global scope.
          // For now, let's regenerate basic data or check if we can access the last generated data.
          // SIMPLIFICATION: We will trigger a quick PDF generation right here if needed.

          let pdfUrl = null;

          // Create PDF Blob
          // Using current fragments and basic connections
          // We won't have the fancy photos unless we stored them. 
          // Let's assume we want to save the *result* of the reconstruction.

          // Upload GLB
          newBtn.textContent = t('recon.uploadingModel');
          const modelFilename = `reconstructed_${timestamp}.glb`;
          const modelRef = ref(storage, `${siteId}/${modelFilename}`);
          await uploadBytes(modelRef, glbBlob);
          const modelDownloadUrl = await getDownloadURL(modelRef);

          // Capture images for PDF
          newBtn.textContent = t('recon.capturingPhotos');
          const { views, fragmentData } = await captureAssemblyImages(viewer3d, selectedFragments);

          // Upload PDF with full image data
          newBtn.textContent = t('recon.uploadingPDF');

          const reconstructionData = {
            views,
            fragments: fragmentData,
            steps: [],
            connections: createSimpleConnections(selectedFragments)
          };

          const pdfBlob = generateAssemblyPDF(
            reconstructionData,
            site.name,
            selectedFragments,
            true // returnBlob
          );

          const pdfFilename = `assembly_${timestamp}.pdf`;
          const pdfRef = ref(storage, `${siteId}/${pdfFilename}`);
          await uploadBytes(pdfRef, pdfBlob);
          const pdfDownloadUrl = await getDownloadURL(pdfRef);

          // Create Asset Record
          await createAsset(siteId, {
            name: 'Reconstructed Assembly (Color)',
            url: modelDownloadUrl, // GLB URL
            pdfUrl: pdfDownloadUrl, // PDF URL
            format: 'glb', // Changed from obj
            size: glbBlob.size,
            description: 'Colored assembly with PDF guide.',
            date: new Date().toISOString()
          });

          toast.success(t('recon.addedToLib'));
          newBtn.textContent = t('recon.added');
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(t('app.error'));
          newBtn.disabled = false;
          newBtn.textContent = t('recon.saveToLib');
        }
      });
    }

    // Add PDF Download button handler
    const pdfBtn = document.getElementById('download-pdf-btn');
    if (pdfBtn) {
      const newPdfBtn = pdfBtn.cloneNode(true);
      pdfBtn.parentNode.replaceChild(newPdfBtn, pdfBtn);

      newPdfBtn.addEventListener('click', async () => {
        try {
          if (selectedFragments.length === 0) {
            toast.warning('Parça seçilmedi');
            return;
          }

          toast.info('Görsel rehber oluşturuluyor... (Lütfen bekleyin)');
          newPdfBtn.disabled = true;
          newPdfBtn.textContent = 'Oluşturuluyor...';

          // Use the extracted capture function
          const { views, fragmentData } = await captureAssemblyImages(viewer3d, selectedFragments);

          const reconstructionData = {
            views,
            fragments: fragmentData,
            steps: [],
            connections: createSimpleConnections(selectedFragments)
          };

          // Generate PDF
          generateAssemblyPDF(reconstructionData, site.name, selectedFragments);
          toast.success('PDF İndirildi');
        } catch (error) {
          console.error('PDF generation error:', error);
          toast.error('PDF oluşturulamadı');
        } finally {
          newPdfBtn.disabled = false;
          newPdfBtn.textContent = '📄 Montaj Şeması İndir';
        }
      });
    }

  } catch (error) {
    console.error('Reconstruction failed:', error);
    statusEl.classList.add('hidden');
    toast.error(`Reconstruction Failed: ${error.message}`);
    alert('Python sunucusunun 8080 portunda çalıştığından emin olun! Çalıştırın: python reconstruction_server.py');
  }
}

/**
 * Capture assembly images for PDF generation
 * @param {Viewer3D} viewer3d - The 3D viewer instance
 * @param {Array} selectedFragments - Array of fragment objects
 * @returns {Promise<{views: Object, fragmentData: Array}>}
 */
async function captureAssemblyImages(viewer3d, selectedFragments) {
  const views = {};
  const fragmentData = [];

  if (!viewer3d) {
    return { views, fragmentData };
  }

  // 1. Capture Individual Fragment Images
  for (let i = 0; i < selectedFragments.length; i++) {
    const frag = selectedFragments[i];
    toast.info(`Fotoğraflanıyor: Parça ${i + 1}/${selectedFragments.length} (Eser #${frag.artifactId})`);

    viewer3d.clearModels();
    try {
      // Load individual model
      const model = await viewer3d.loadModel(frag.url, frag.format || 'obj');

      // Color it for the photo!
      const color = FRAGMENT_COLORS[i % FRAGMENT_COLORS.length];
      viewer3d.setModelColor(model, color);

      // Apply smart camera positioning to frame the fragment properly
      viewer3d.setCameraPosition('iso', model);
      await new Promise(r => setTimeout(r, 100)); // Render wait

      // Capture thumbnail
      const thumb = viewer3d.captureScreenshot(400, 400);

      fragmentData.push({
        ...frag,
        image: thumb,
        colorHex: color
      });
    } catch (e) {
      console.error(`Failed to capture fragment ${frag.artifactId}`, e);
      fragmentData.push({ ...frag, colorHex: FRAGMENT_COLORS[i % FRAGMENT_COLORS.length] });
    }
  }

  // 2. Load and Color the Composite Assembly
  toast.info('Montaj birleştiriliyor ve boyanıyor...');
  viewer3d.clearModels();

  const compositeItems = selectedFragments.map((frag, i) => ({
    url: frag.url,
    format: frag.format || 'obj',
    artifactId: frag.artifactId,
    name: frag.name
  }));

  const group = await viewer3d.loadCompositeModel(compositeItems);

  // Apply Colors
  group.traverse((child) => {
    if (child.isMesh && child.userData.artifactId) {
      const fIndex = selectedFragments.findIndex(f => f.artifactId === child.userData.artifactId);
      if (fIndex >= 0) {
        const colorFunc = fragmentData.find(fd => fd.id === selectedFragments[fIndex].id);
        const finalColor = colorFunc ? colorFunc.colorHex : FRAGMENT_COLORS[fIndex % FRAGMENT_COLORS.length];
        viewer3d.setModelColor(child, finalColor);
      }
    }
  });

  // 3. Capture Global Views (5 Angles)
  toast.info('Genel görünümler kaydediliyor...');

  const angles = ['iso', 'front', 'top', 'right', 'back'];
  for (const angle of angles) {
    viewer3d.setCameraPosition(angle, group);
    await new Promise(r => setTimeout(r, 150));
    views[angle] = viewer3d.captureScreenshot(800, 600);
  }

  // Restore
  viewer3d.resetCamera();

  return { views, fragmentData };
}

/**
 * Delay helper
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Escape HTML
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Cleanup on view exit
 */
export function cleanupReconstructView() {
  if (viewer3d) {
    viewer3d.dispose();
    viewer3d = null;
  }
}
